import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, ShieldAlert, Check, TestTube2 } from "lucide-react";

export default function PurgeUsersPanel() {
  const [emails, setEmails] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);

  const parseEmails = () => emails.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean);

  const runDry = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/purge-users", { keep_emails: parseEmails(), dry_run: true });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "preview failed");
    } finally { setBusy(false); }
  };

  const runReal = async () => {
    if (!preview) return;
    if (!window.confirm(`Delete ${preview.will_delete_users} users + ${preview.will_delete_subscribers} subscribers?\n\nKeeping ${preview.keeping_users} users + ${preview.keeping_subscribers} subscribers.\n\nThis cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/purge-users", { keep_emails: parseEmails(), dry_run: false });
      toast.success(`deleted ${data.deleted_users} users + ${data.deleted_subscribers} subscribers`);
      setPreview(null);
      setEmails("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "purge failed");
    } finally { setBusy(false); }
  };

  const purgeTestTransactions = async () => {
    if (!window.confirm("Delete every unlock + transaction created in Stripe TEST mode (session_id starts with cs_test_)? This fixes stats mismatches after flipping to live mode.")) return;
    setTestBusy(true);
    try {
      const { data } = await api.post("/admin/purge-test-transactions");
      toast.success(`removed ${data.deleted_unlocks} test unlocks + ${data.deleted_transactions} test transactions`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "purge failed");
    } finally { setTestBusy(false); }
  };

  return (
    <div className="mb-10" data-testid="purge-panel">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <ShieldAlert className="text-hotpink" size={22} />
        <h2 className="brush-title text-3xl md:text-4xl text-white">CLEAN THE <span className="text-hotpink">LIST.</span></h2>
      </div>
      <div className="bg-coal border border-red-500/40 p-5">
        <p className="font-outfit text-white/70 text-sm mb-3">
          Paste every email you want to <strong className="text-hotpink">keep</strong> — one per line or comma-separated. Everyone else's user account + subscription is deleted. Admin account is always kept. Preview counts first, then confirm.
        </p>
        <textarea
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          placeholder={"careyes02@yahoo.com\nkevgrisales47@gmail.com\nitamar.solell@gmail.com"}
          rows={6}
          data-testid="purge-emails"
          className="w-full bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none resize-none"
        />
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <button onClick={runDry} disabled={busy || !parseEmails().length} data-testid="purge-preview" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-outfit font-bold uppercase tracking-widest text-xs px-4 py-2 disabled:opacity-60">
            <Users size={13}/> {busy ? "..." : "preview counts"}
          </button>
          {preview && (
            <button onClick={runReal} disabled={busy} data-testid="purge-confirm" className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-outfit font-extrabold uppercase tracking-widest text-xs px-4 py-2 disabled:opacity-60">
              <Check size={13}/> confirm delete
            </button>
          )}
        </div>
        {preview && (
          <div className="mt-4 bg-ink border border-white/15 p-4 font-outfit text-sm" data-testid="purge-preview-box">
            <div className="text-hotpink text-[10px] uppercase tracking-widest mb-2">preview</div>
            <div className="text-white/80">Keeping <span className="text-hotpink font-bold">{preview.keeping_users}</span> users + <span className="text-hotpink font-bold">{preview.keeping_subscribers}</span> subscribers</div>
            <div className="text-white/80">Will delete <span className="text-red-400 font-bold">{preview.will_delete_users}</span> users + <span className="text-red-400 font-bold">{preview.will_delete_subscribers}</span> subscribers</div>
            <details className="mt-2 text-white/50 text-xs">
              <summary className="cursor-pointer hover:text-hotpink">emails being kept ({preview.keep_emails.length})</summary>
              <ul className="mt-2 space-y-1">
                {preview.keep_emails.map((e) => <li key={e}>· {e}</li>)}
              </ul>
            </details>
          </div>
        )}
      </div>

      <div className="mt-4 bg-coal border border-yellow-500/40 p-4 flex items-center gap-3 flex-wrap" data-testid="purge-test-tx-panel">
        <TestTube2 className="text-yellow-400 flex-shrink-0" size={18} />
        <div className="flex-1 min-w-[200px]">
          <div className="font-outfit text-white text-sm">Post-live-mode cleanup</div>
          <div className="font-outfit text-white/50 text-xs">Wipe any unlocks / transactions that came from Stripe test mode (session id starts with <span className="font-mono">cs_test_</span>). Runs once after flipping to live keys — makes admin stats match your real Stripe count.</div>
        </div>
        <button onClick={purgeTestTransactions} disabled={testBusy} data-testid="purge-test-tx-btn" className="bg-yellow-500 hover:bg-yellow-600 text-black font-outfit font-extrabold uppercase tracking-widest text-xs px-4 py-2 disabled:opacity-60">
          {testBusy ? "..." : "clean test data"}
        </button>
      </div>
    </div>
  );
}
